import React, { useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Star, Users, Award, TrendingUp, Calendar, Shield, Eye, Zap, CheckCircle } from 'lucide-react';

export default function PromoVideoSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    // Trigger animation on component mount
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const stats = [
    { icon: Users, value: '1 Lakh+', label: 'Simultaneous Candidate Exam ', color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
    { icon: Shield, value: '100%', label: 'Secure Exams', color: 'text-green-400', bgColor: 'bg-green-500/20' },
    { icon: Award, value: '24/7', label: 'Technical Support', color: 'text-purple-400', bgColor: 'bg-purple-500/20' },
    { icon: Eye, value: 'Live', label: 'Proctoring & Review', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' }
  ];

  const floatingFeatures = [
    { icon: CheckCircle, title: 'Face Verification', subtitle: 'AI-powered identity check', color: 'text-emerald-400', delay: 'delay-700' },
    { icon: Shield, title: 'Secure Environment', subtitle: 'Tamper-proof system', color: 'text-blue-400', delay: 'delay-900' },
    { icon: Zap, title: 'Review Mode', subtitle: 'Resume & review anytime', color: 'text-yellow-400', delay: 'delay-1100' },
    { icon: Eye, title: 'Live Monitoring', subtitle: 'Real-time supervision', color: 'text-purple-400', delay: 'delay-1300' }
  ];

      const handleSmoothScroll = (e: any, href: any) => {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
     
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
     
        }
    };

  return (
    <section className="relative py-10 overflow-hidden bg-gray-900 ">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-purple-600 rounded-full filter blur-3xl opacity-40 mix-blend-multiply"></div>
        <div className="absolute top-0 right-1/4 w-72 h-72 bg-pink-600 rounded-full filter blur-3xl opacity-40 mix-blend-multiply"></div>
        <div className="absolute bottom-0 left-1/3 w-72 h-72 bg-blue-600 rounded-full filter blur-3xl opacity-40 mix-blend-multiply"></div>
      </div>
      {/* Enhanced Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Large animated orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-3xl animate-pulse-slow delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/5 rounded-full blur-3xl animate-spin-slow"></div>
        
        {/* Animated gradient waves */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 via-transparent to-purple-600/20 animate-wave"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-l from-purple-600/20 via-transparent to-blue-600/20 animate-wave-reverse"></div>
        </div>
      </div>

      {/* Enhanced Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className={`absolute rounded-full animate-float-random ${
              i % 3 === 0 ? 'w-3 h-3 bg-blue-400/30' : 
              i % 3 === 1 ? 'w-2 h-2 bg-purple-400/40' : 
              'w-1 h-1 bg-white/50'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          ></div>
        ))}
      </div>

      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-grid-pattern animate-grid-move"></div>
      </div>

      <div className="relative w-[90%] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header Section */}
        <div className={`text-center mb-2 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-full border border-blue-400/30 mb-6 backdrop-blur-sm animate-glow">
            <Play className="h-5 w-5 text-blue-400 mr-2 animate-pulse" />
            <span className="text-blue-100 font-medium">Watch Our Platform in Action</span>
          </div>
          
          <h2 className="text-xl md:text-5xl font-bold text-white mb-1 leading-tight">
            Experience the{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient-x">
              Future
            </span>{' '}
            
            of Examinations
          </h2>
          
          <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed animate-fade-in-up mt-8">
            Discover how QCBT revolutionizes online testing with cutting-edge AI technology, 
            unbreakable security, and seamless user experience for 1 lakh+ simultaneous exam
          </p>
        </div>

        {/* Enhanced Main Video Container */}
        <div className={`relative lg:mt-10 max-w-6xl mx-auto transition-all duration-1000 delay-300 transform ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          {/* Multiple Glowing Border Effects */}
          {/* <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-3xl blur-lg opacity-75 animate-pulse-glow"></div>
          <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 rounded-3xl blur opacity-50 animate-rotate-slow"></div> */}
          
          <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl border border-white/10 mt-4">
            {/* Enhanced Video Player */}
            <div className="relative group">
              <iframe
                className="w-full h-64 md:h-96 lg:h-[500px] transition-all duration-500 group-hover:scale-[1.02]   !z-[9999]"
                src="https://www.youtube.com/embed/gZ-3B0DIwFs?si=example&rel=0&modestbranding=1&showinfo=0&autoplay=0&mute=1"
                
                
                
                
                
                         title="QCBT Platform Promotional Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
              
              ></iframe>
              
              {/* Enhanced Video Overlay Controls */}
              {/* <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button 
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-all duration-300 transform hover:scale-110 animate-bounce-subtle"
                    >
                      {isPlaying ? (
                        <Pause className="h-7 w-7 text-white" />
                      ) : (
                        <Play className="h-7 w-7 text-white ml-1" />
                      )}
                    </button>
                    
                    <button 
                      onClick={() => setIsMuted(!isMuted)}
                      className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-all duration-300 transform hover:scale-110"
                    >
                      {isMuted ? (
                        <VolumeX className="h-6 w-6 text-white" />
                      ) : (
                        <Volume2 className="h-6 w-6 text-white" />
                      )}
                    </button>
                    
                    <div className="text-white text-sm bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                      <span className="animate-pulse">●</span> LIVE DEMO
                    </div>
                  </div>
                  
                  <button className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition-all duration-300 transform hover:scale-110">
                    <Maximize className="h-6 w-6 text-white" />
                  </button>
                </div>
              </div> */}

              {/* Animated corner indicators */}
              <div className="absolute top-4 left-4 w-4 h-4 border-l-2 border-t-2 border-blue-400 animate-pulse"></div>
              <div className="absolute top-4 right-4 w-4 h-4 border-r-2 border-t-2 border-blue-400 animate-pulse delay-300"></div>
              <div className="absolute bottom-4 left-4 w-4 h-4 border-l-2 border-b-2 border-blue-400 animate-pulse delay-600"></div>
              <div className="absolute bottom-4 right-4 w-4 h-4 border-r-2 border-b-2 border-blue-400 animate-pulse delay-900"></div>
            </div>
          </div>

          {/* Enhanced Floating Feature Cards
          {floatingFeatures.map((feature, index) => (
            <div 
              key={index}
              className={`absolute transition-all duration-1000 ${feature.delay} transform ${
                isVisible ? 'opacity-100 translate-x-0' : 
                index % 2 === 0 ? 'opacity-0 -translate-x-10' : 'opacity-0 translate-x-10'
              } ${
                index === 0 ? '-left-8 top-1/4' :
                index === 1 ? '-right-8 top-[35%]' :
                index === 2 ? '-left-12 bottom-[10%]' :
                '-right-12 bottom-[10%]'
              }`}
            >
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 animate-float shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:scale-105 group">
                <div className="flex items-center space-x-3 mb-2">
                  <div className={`w-8 h-8 ${feature.color.replace('text-', 'bg-').replace('-400', '-500/20')} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className={`h-4 w-4 ${feature.color}`} />
                  </div>
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                </div>
                <div className="text-white font-semibold text-sm mb-1">{feature.title}</div>
                <div className="text-blue-100 text-xs">{feature.subtitle}</div>
              </div>
            </div>
          ))} */}
        </div>

        {/* Enhanced Stats Section */}
        <div className={`mt-10 max-w-7xl mx-auto transition-all duration-1000 delay-1300 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className={`inline-flex items-center justify-center w-16 h-16 ${stat.bgColor} backdrop-blur-sm rounded-2xl mb-4 group-hover:scale-110 transition-all duration-300 animate-bounce-subtle border border-white/10`} style={{ animationDelay: `${index * 200}ms` }}>
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
                <div className="text-2xl font-bold text-white mb-2 group-hover:scale-110 transition-transform duration-300 animate-count-up">
                  {stat.value}
                </div>
                <div className="text-blue-200 text-xs font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced Call to Action */}
        {/* <div className={`text-center mt-12 transition-all duration-1000 delay-1500 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a 
          href="#contact"
            onClick={(e)=>handleSmoothScroll(e,"#contact")}
            className="inline-flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-medium rounded-lg px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-300 space-x-4"
          >
            <span>Book A Free Demo</span>
            <Calendar className="h-5 w-5 " />
          </a>
          <p className="text-blue-200 text-sm mt-4 animate-fade-in">Get personalized demonstration • No commitment required </p>
        </div> */}
      </div>
    </section>
  );
}